import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { requestOpenAddCoinModal } from 'utils/onboardingWalkthrough';

const zoneLabelSx = {
  color: 'rgba(149, 242, 5, 0.85)',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  mb: 0.75,
};

const zoneSx = {
  border: '1px dashed rgba(149, 242, 5, 0.32)',
  borderRadius: 2,
  bgcolor: 'rgba(255, 255, 255, 0.02)',
  p: { xs: 1.5, sm: 2 },
};

const zoneHintSx = {
  color: 'rgba(255,255,255,0.5)',
  fontSize: 12,
  lineHeight: 1.45,
};

function Zone({ label, hint, children, sx = {} }) {
  return (
    <Box sx={{ ...zoneSx, ...sx }}>
      <Typography sx={zoneLabelSx}>{label}</Typography>
      {hint && (
        <Typography sx={{ ...zoneHintSx, mb: children ? 1 : 0 }}>{hint}</Typography>
      )}
      {children}
    </Box>
  );
}

function GhostCoinRow({ symbol, name }) {
  return (
    <Grid
      container
      sx={{
        borderBottom: '1px dashed rgba(255,255,255,0.12)',
        py: 1.25,
        px: 1,
        opacity: 0.65,
      }}
    >
      <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '1px dashed rgba(255,255,255,0.25)',
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        />
      </Grid>
      <Grid item xs={2.5}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>{symbol}</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{name}</Typography>
      </Grid>
      <Grid item xs={3.5}>
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>Live price</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, mt: 0.5 }}>Avg buy</Typography>
      </Grid>
      <Grid item xs={4} sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: 'rgba(149, 242, 5, 0.5)', fontSize: 11 }}>P&amp;L</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mt: 0.5 }}>Invested</Typography>
      </Grid>
    </Grid>
  );
}

export function AssetsEmptyListPreview() {
  return (
    <Zone
      label="Your coin list"
      hint="Each row shows symbol, live price, average buy, P&L, and XGain. Tap a coin to open details."
      sx={{ mx: 1, mt: 1 }}
    >
      <GhostCoinRow symbol="BTC" name="Bitcoin" />
      <GhostCoinRow symbol="ETH" name="Ethereum" />
      <GhostCoinRow symbol="SOL" name="Solana" />
      <Typography sx={{ ...zoneHintSx, mt: 1.5, fontStyle: 'italic', textAlign: 'center' }}>
        Preview only — add a coin to populate this list
      </Typography>
    </Zone>
  );
}

export function AssetsEmptyDetailPreview({ onAddClick, compact = false }) {
  const handleAdd = () => {
    if (onAddClick) {
      onAddClick();
      return;
    }
    requestOpenAddCoinModal();
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: compact ? 'auto' : '100%',
        px: { xs: 2, sm: 3 },
        py: compact ? 2 : { xs: 3, md: 4 },
        pb: compact ? 10 : { xs: 12, md: 4 },
      }}
    >
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 800,
          fontSize: { xs: '1.15rem', sm: '1.3rem' },
          mb: 0.5,
        }}
      >
        Coin detail panel
      </Typography>
      <Typography sx={{ ...zoneHintSx, mb: 2.5, maxWidth: 560 }}>
        When you select a coin from the list, this area fills with charts, exit targets, and performance data.
      </Typography>

      <Stack spacing={1.75}>
        <Zone
          label="Coin header"
          hint="Symbol, name, rank, current price, quantity, worth, and XGain multiplier."
        />

        <Zone
          label="Fibonacci chart & exit sliders"
          hint="Drag the four sell targets on the Fib chart to plan exits at key levels."
          sx={{ minHeight: { xs: 120, md: 160 } }}
        />

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={7}>
            <Zone
              label="Sell targets table"
              hint="Sell 1–4 prices, quantities, and profit at each exit."
              sx={{ height: '100%', minHeight: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Zone
              label="XGain gauge"
              hint="Visual multiplier vs your average buy."
              sx={{ height: '100%', minHeight: 100 }}
            />
          </Grid>
        </Grid>

        <Zone
          label="Exchange links & activity"
          hint="Quick links to trade on your exchanges plus buy/sell activity history."
          sx={{ minHeight: 72 }}
        />
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          onClick={handleAdd}
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            color: '#95F205',
            borderColor: 'rgba(149, 242, 5, 0.55)',
            borderRadius: '999px',
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': {
              borderColor: '#95F205',
              bgcolor: 'rgba(149, 242, 5, 0.08)',
            },
          }}
        >
          Add your first coin
        </Button>
      </Box>
    </Box>
  );
}

export default function AssetsEmptyLayout({ onAddClick, compact = false }) {
  if (compact) {
    return (
      <Box sx={{ px: 1 }}>
        <AssetsEmptyDetailPreview onAddClick={onAddClick} compact />
      </Box>
    );
  }

  return <AssetsEmptyDetailPreview onAddClick={onAddClick} />;
}
